import React, { useState, useEffect } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Paper, Typography, Box, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import { analyticsApi } from '../../services/api';

interface CashFlowData {
  month: string;
  income: number;
  expenses: number;
  net: number;
  year: number;
}

interface CashFlowResponse {
  data: CashFlowData[];
}

const MonthlyCashFlowChart: React.FC = () => {
  const [data, setData] = useState<CashFlowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonths, setSelectedMonths] = useState<number>(12);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const monthOptions = [
    { value: 6, label: '6 Months' },
    { value: 12, label: '12 Months' },
    { value: 18, label: '18 Months' },
    { value: 24, label: '24 Months' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response: CashFlowResponse = await analyticsApi.getMonthlyCashFlow(selectedMonths);
        setData(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load cash flow data');
        console.error('Error fetching cash flow data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMonths]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            p: isMobile ? 1 : 2,
            maxWidth: isMobile ? 200 : 'none'
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color, fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
              {entry.name}: {formatCurrency(entry.value)}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (data.length === 0) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 350, sm: 400, md: 450 } }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Monthly Cash Flow
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <Typography variant="body1" color="text.secondary">
            No cash flow data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        <Typography variant="h6" sx={{ 
          color: 'primary.main', 
          fontWeight: 'bold', 
          fontSize: { xs: '1rem', md: '1.25rem' } 
        }}>
          Monthly Cash Flow
        </Typography>
        <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedMonths}
            label="Period"
            onChange={(e) => setSelectedMonths(Number(e.target.value))}
          >
            {monthOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ 
        height: { xs: 280, sm: 320, md: 380 }, 
        width: '100%',
        mt: 2,
        overflow: 'hidden'
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart 
            data={data} 
            margin={{ 
              top: 10, 
              right: isMobile ? 5 : 15, 
              left: isMobile ? 5 : 10, 
              bottom: isMobile ? 60 : 40 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month"
              stroke="#666"
              fontSize={isMobile ? 10 : 12}
              angle={isMobile ? -45 : 0}
              textAnchor={isMobile ? "end" : "middle"}
              height={isMobile ? 80 : 60}
              interval={isMobile ? 1 : 0}
            />
            <YAxis 
              tickFormatter={(value) => isMobile ? `${Math.round(value/1000)}k` : formatCurrency(value)}
              stroke="#666"
              fontSize={isMobile ? 10 : 12}
              width={isMobile ? 40 : 80}
            />
            <Tooltip content={<CustomTooltip />} />
            {!isMobile && <Legend />}
            <Bar 
              dataKey="income" 
              fill="#4caf50" 
              name="Income"
              radius={[2, 2, 0, 0]}
            />
            <Bar 
              dataKey="expenses" 
              fill="#f44336" 
              name="Expenses"
              radius={[2, 2, 0, 0]}
            />
            <Line 
              type="monotone" 
              dataKey="net" 
              stroke="#2196f3" 
              strokeWidth={isMobile ? 2 : 3}
              name="Net Cash Flow"
              dot={{ fill: '#2196f3', strokeWidth: 1, r: isMobile ? 3 : 4 }}
              activeDot={{ r: isMobile ? 4 : 6, stroke: '#2196f3', strokeWidth: 2 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      {isMobile && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 2, 
          mt: 1,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Income</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: 0.5 }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Expenses</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 2, bgcolor: '#2196f3', borderRadius: 1 }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Net Flow</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MonthlyCashFlowChart;